import React, { createContext, useState, useEffect } from 'react'
import { checkStorageOptions, retrieveFromCache, storeInCache } from '../../Utils'
import { logEvent } from '../../Utils'
import { ANALYTICS_TAGS } from '../../Constant'
import { executeWithRetry } from '../../Utils/networking/networkHelpers'
import type { StorageContextType } from './types'

export const StorageContext = createContext<StorageContextType | null>(null)

export const StorageProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [storageStatuses, setStorageStatuses] = useState({
    indexedDB: false,
    local: false,
    session: false,
    cookies: false,
  })
  const [isStorageReady, setIsStorageReady] = useState(false)

  useEffect(() => {
    const initializeStorage = () => {
      const storageOptions = checkStorageOptions()
      setStorageStatuses(storageOptions)
      setIsStorageReady(true)
    }

    initializeStorage()
  }, [])

  const store = async (key: string, item: any): Promise<void> => {
    const result = await executeWithRetry(async () => {
      await storeInCache(key, item, storageStatuses)
      return true
    }, `store data with key: ${key}`)

    if (result === null) {
      logEvent(ANALYTICS_TAGS.storage_failure, {
        context: 'storage_retry_max_attempts_exceeded_store',
        key,
      })
    }
  }

  const fetchAndCache = async <T,>(
    key: string,
    fetcher: () => Promise<T>,
    onUpdate: (data: T) => void,
    ttlMs: number = 1000 * 60 * 5
  ): Promise<void> => {
    let networkRequestFinished = false

    const cacheOperation = retrieveFromCache(key, storageStatuses, ttlMs).then(
      (result: { data: T | null }) => {
        if (result?.data && !networkRequestFinished) {
          onUpdate(result.data)
        }
      }
    )

    const networkOperation = fetcher()
      .then(async (freshData: T) => {
        networkRequestFinished = true
        onUpdate(freshData)
        await store(key, freshData)
        return freshData
      })
      .catch((error) => {
        networkRequestFinished = true
        logEvent(ANALYTICS_TAGS.network_error, {
          context: 'network_fetch_failed',
          key,
          error: error.message,
        })
        return null
      })

    await Promise.all([cacheOperation, networkOperation])
  }

  return (
    <StorageContext.Provider
      value={{
        storageStatuses,
        isStorageReady,
        store,
        fetchAndCache,
      }}
    >
      {children}
    </StorageContext.Provider>
  )
}
